import type { Navigation } from '@nakedplatform/business';
import Router from 'next/router';
import { ParsedUrlQueryInput, stringify } from 'querystring';
import { Routes } from 'routes';
import { getAndClearQuoteReferrer } from '../quote/helpers';

// URL needs a base to parse relative routes; `.invalid` is reserved and can never be a real destination.
const ROUTE_BASE_URL = 'https://routing.invalid';
const MIGRATION_ROUTED_PATHS = new Set<string>([
  Routes.BASKET,
  Routes.CHOOSE_PRODUCT,
  Routes.QuoteBuilding,
  Routes.QuoteCar,
  Routes.QuoteHousehold,
  Routes.QuotePet,
  Routes.QuoteRenter,
  Routes.QuoteSingleItemUnderwritten,
  '/communication-preferences',
]);

export const navigation: Navigation = {
  goBack: Router.back,
  goHome: (ignoreBasePath = true) => {
    ignoreBasePath ? window.location.assign('/') : Router.push('/');
  },
  gotoRoute: (pathname: string, query: ParsedUrlQueryInput, ignoreBasePath = false) => {
    // Some destinations (e.g. Routes.Summary) live in the `web` app at the
    // root, while we're served from the `platform` app under a basePath.
    // Next's client router can't hop basePaths, so we have to do a full-page
    // navigation. Use `assign` (not `replace`) so the current page stays in
    // the history stack and the browser back button works as users expect.
    const shouldUseDocumentNavigation =
      ignoreBasePath || pathname === Routes.Summary || isMigrationRoutedPath(pathname);
    const queryString = stringify(query);
    const url = queryString ? `${pathname}?${queryString}` : pathname;

    if (shouldUseDocumentNavigation) {
      window.location.assign(url);
    } else {
      Router.push({ pathname, query });
    }
  },
};

/**
 * Checks whether navigation must reach the server-owned migration router.
 *
 * Platform must not use its client router for these paths because that would
 * bypass the Web feature flag and keep rendering the Platform application.
 *
 * @param destination - Route or URL to classify
 */
export function isMigrationRoutedPath(destination: string): boolean {
  const url = new URL(destination, ROUTE_BASE_URL);

  return MIGRATION_ROUTED_PATHS.has(url.pathname);
}
/**
 * A function which returns a function that calls navigation gotoRoute
 *
 * @param route - the route we want to navigate the user to
 * @param ignoreBasePath - whether to ignore the base path
 */
export function goToRoute(route: Routes, ignoreBasePath = false) {
  return () => {
    navigation.gotoRoute(route, {}, ignoreBasePath);
  };
}
/**
 * Smart back navigation that uses stored referrer with fallback to product page
 *
 * @param fallbackRoute - the product page route to use if no referrer is available
 * @param ignoreBasePath - whether to ignore the base path
 */
export function goToReferrerOrRoute(fallbackRoute: Routes, ignoreBasePath = true) {
  return () => {
    const referrer = getAndClearQuoteReferrer();

    if (referrer) {
      // Use referrer page
      navigation.gotoRoute(referrer, {}, ignoreBasePath);
    } else {
      // Fallback to product page
      navigation.gotoRoute(fallbackRoute, {}, ignoreBasePath);
    }
  };
}
